import None from "98Resources/None.svg";

import { AppContext } from "01Data/AppData";
import {
  AndonParamName,
  BuildingInfo,
  FilterSetting,
  Pico3,
  Sensor,
} from "01Data/DataTypes";
import {
  AndonTitleBuilding,
  AndonTitlePico3,
  AndonTitleSensor,
} from "97Parts/Andon/AndonTitle";
import "97Parts/ScrollBarStyle.css";
import { NoteText } from "97Parts/Styles";
import {
  LeakageExpression,
  SensorStatusType,
  TypeDetails,
  TypeDetailsList,
} from "99Tools/utils";
import {
  Close,
  EmojiTransportation,
  OpenInNew,
  Sensors,
} from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  IconButton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { ANDON_HEADER_COLOR } from "Settings";

const Section = styled("div")(({ theme }) => ({
  marginBottom: "20px",
}));

const TableRange = styled("div")(({ theme }) => ({
  width: "100%",
  overflowX: "auto",
}));

const Header = styled("th")(({ theme }) => ({
  fontSize: "0.8rem",
  borderCollapse: "collapse",
  border: "solid 1px #aaa",
  backgroundColor: ANDON_HEADER_COLOR,
  color: theme.palette.common.white,
  whiteSpace: "nowrap",
}));

const IconCell = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const Cell = styled("td")(({ theme }) => ({
  borderCollapse: "collapse",
  border: "solid 1px #aaa",
  padding: "4px",
  minWidth: "12vh",
}));

const CellCentered = styled("div")(({ theme }) => ({
  // display: "flex",
  // justifyContent: "center",
  textAlign: "center",
}));

const ButtonDlg = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

const DialogHeader = styled(Typography)(({ theme }) => ({
  padding: "15px",
  paddingRight: "50px",
  backgroundColor: theme.palette.primary.main,
  color: "white",
}));

const ListItem = styled("p")(() => ({
  margin: 0,
  padding: 0,
}));

type DataDetailDlgKind = "init" | "building" | "sensor" | "pico3";
export class DataDetailDlg {
  public kind: DataDetailDlgKind = "init";
  public title;
  public StatusUpdatedAt;
  public UseStarted: string;
  public Expiration: string;
  public exp: SensorStatusType;
  public details: TypeDetailsList;
  constructor(
    kind: DataDetailDlgKind = "init",
    title = "",
    StatusUpdatedAt = "",
    UseStarted = "",
    Expiration = "",
    exp = LeakageExpression["DUMMY"],
    details: TypeDetailsList = []
  ) {
    this.kind = kind;
    this.title = title;
    this.StatusUpdatedAt = StatusUpdatedAt;
    this.UseStarted = UseStarted;
    this.Expiration = Expiration;
    this.exp = exp;
    this.details = details;
  }
  public GetIconText() {
    switch (this.kind) {
      case "building":
        return this.exp.textBuilding;
      case "sensor":
        return this.exp.textSensor;
      case "pico3":
        return this.exp.textPico3;
      default:
        return "";
    }
  }
}

export const DetailForm = ({
  bShowDlg,
  dlgData,
  setShowDlg,
  buildingsRedraw,
}: {
  bShowDlg: boolean;
  dlgData: DataDetailDlg;
  setShowDlg: (bShow: boolean) => void;
  buildingsRedraw: () => void;
}) => {
  const andon = useContext(AppContext).GetAndonData();
  const isSmartphone = useMediaQuery({
    query: "(max-width: 600px)",
  });

  const handleClose = () => {
    setShowDlg(false);
    andon.Sort(andon.sortParamBuilding);
    buildingsRedraw();
  };

  const items = dlgData.details.map((item: TypeDetails, index: number) => (
    <tr key={index}>
      <Cell>
        <CellCentered>{item.title}</CellCentered>
      </Cell>
      <Cell>
        <CellCentered>
          {Array.isArray(item.value)
            ? item.value.map((v) => <ListItem key={v}>{v}</ListItem>)
            : item.value}
        </CellCentered>
      </Cell>
    </tr>
  ));

  return (
    <Dialog fullWidth key="detail" open={bShowDlg} onClose={handleClose}>
      <DialogHeader sx={{ overflowWrap: "anywhere" }}>
        {dlgData.title}
      </DialogHeader>
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "white",
        }}
      >
        <Close />
      </IconButton>
      <div style={{ padding: "0 15px 0" }}>
        <div style={{ display: "flex", padding: "15px 0 15px", gap: "10px" }}>
          <img
            src={dlgData.exp.icon}
            alt={dlgData.GetIconText()}
            title={dlgData.GetIconText()}
            width={isSmartphone ? "60px" : "80px"}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <NoteText sx={{ whiteSpace: "nowrap" }}>ステータス</NoteText>
            <div>{dlgData.GetIconText()}</div>
          </div>
          {dlgData.details.filter(
            (item) => item.title === "種別" && item.value === "LTE"
          ).length > 0 ? (
            <Table size="small" sx={{ width: "40%" }}>
              <TableBody>
                <TableRow>
                  <TableCell variant="body" sx={{ whiteSpace: "nowrap" }}>
                    最終受信:
                  </TableCell>
                  <TableCell
                    align="center"
                    variant="body"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {dlgData.StatusUpdatedAt}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant="body" sx={{ whiteSpace: "nowrap" }}>
                    使用開始:
                  </TableCell>
                  <TableCell
                    align="center"
                    variant="body"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {dlgData.UseStarted}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant="body" sx={{ whiteSpace: "nowrap" }}>
                    使用期限:
                  </TableCell>
                  <TableCell
                    align="center"
                    variant="body"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {dlgData.Expiration}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : null}
        </div>

        <Stack direction="row" alignItems="center">
          {dlgData.kind === "building" ? (
            <>
              <EmojiTransportation
                fontSize="small"
                sx={(theme) => ({ color: theme.palette.grey[500] })}
              />
              <Typography>建物情報</Typography>
            </>
          ) : (
            <>
              <Sensors
                fontSize="small"
                sx={(theme) => ({ color: theme.palette.grey[500] })}
              />
              <Typography>デバイス情報</Typography>
            </>
          )}
        </Stack>
        {/* <TableRange className="scrollBar"> */}
        <table
          style={{
            borderCollapse: "collapse",
            border: "solid 1px #aaa",
            width: "100%",
          }}
        >
          <tbody style={{ textWrap: "wrap", overflowWrap: "anywhere" }}>
            <tr>
              <Header>名称</Header>
              <Header>値</Header>
            </tr>
            {items}
          </tbody>
        </table>
        {/* </TableRange> */}

        <DialogActions>
          <ButtonDlg
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleClose}
          >
            キャンセル
          </ButtonDlg>
        </DialogActions>
      </div>
    </Dialog>
  );
};

export const BuildingSection = ({
  setShowDlg,
  setDlgData,
  redraw,
  sensorsRedraw,
  pico3Redraw,
}: {
  setShowDlg: (bShow: boolean) => void;
  setDlgData: (data: DataDetailDlg) => void;
  redraw: () => void;
  sensorsRedraw: () => void;
  pico3Redraw: () => void;
}) => {
  const andon = useContext(AppContext).GetAndonData();
  const buildings = andon.buildings;
  const [currentBuildingFilter, setCurrentBuildingFilter] = useState("");

  const isSmartphone = useMediaQuery({
    query: "(max-width: 600px)",
  });

  if (buildings.length === 0) {
    // 建物がない
    return null;
  }

  const handleShowMap = (url: string) => {
    window.open(url, "_blank");
  };

  const updateFilter = (f: FilterSetting, name: string) => {
    if (f.items.filter((item) => item.name === name).length === 0) {
      // 選択した建物のレコードが存在しない
      f.items.forEach((item) => {
        item.bShow = name === currentBuildingFilter; // 前回と同じなら解除
      });
    } else if (
      f.items[0 /*すべて選択*/].bShow ||
      !f.items.filter((item) => item.name === name)[0].bShow
    ) {
      // すべて選択 On または選択した建物が表示状態でない

      // 選択した建物しかない -> すべて選択
      if (f.items.length <= 2) {
        f.items.forEach((item) => {
          item.bShow = true;
        });
        return;
      }

      //  選択した建物のみ残す
      f.items.forEach((item) => {
        item.bShow = item.name === name;
      });
    } else if (f.items.length <= 2) {
      // 選択した建物しかない → なにもしない
      return;
    } else {
      // すべて選択
      f.items.forEach((item) => {
        item.bShow = true;
      });
    }
  };

  /**
   * 建物一覧 建物名セルクリックイベントハンドラ
   *
   * LTEデバイス一覧およびPico3一覧で指定した建物のデバイスのみ表示するように
   * 絞り込みをかける
   * @param eventTarget
   * @returns
   */
  const handleCellClick = (eventTarget: any) => {
    // LTE デバイス一覧
    updateFilter(andon.filter.sensors[2 /*建物名*/], eventTarget.textContent);
    andon.ApplyFilter("sensors");
    sensorsRedraw();

    // Pico3 一覧
    updateFilter(andon.filter.pico3[2 /*建物名*/], eventTarget.textContent);
    andon.ApplyFilter("pico3");
    pico3Redraw();

    // 保持するフィルタ条件を更新
    andon.Sort(andon.sortParamBuilding);
    redraw();
    if (currentBuildingFilter !== eventTarget.textContent) {
      // 現在のフィルタ条件を保持
      setCurrentBuildingFilter(eventTarget.textContent);
    } else {
      // 現在のフィルタ条件を解除（同じ建物を2回クリック）
      setCurrentBuildingFilter("");
    }
  };

  /**
   * 建物一覧 ステータスセルクリックイベントハンドラ
   * @param index
   * @param exp
   */
  const handleShowDetail = (index: number, exp: SensorStatusType) => {
    andon.Sort(andon.sortParamBuilding);
    redraw();
    const data = new DataDetailDlg(
      "building",
      buildings[index].BuildingName,
      "",
      "",
      "",
      exp,
      buildings[index].GetDetails()
    );
    setShowDlg(true);
    setDlgData(data);
  };

  const rows = buildings
    .map((building: BuildingInfo, index: number) => {
      if (!building.bShowTop) {
        return null;
      }
      return (
        <TableRow hover={true} key={building.BuildingId}>
          <Cell
            sx={{ cursor: "pointer" }}
            onClick={() => handleShowDetail(index, building.sensorStatus)}
          >
            <IconCell>
              <img
                src={building.sensorStatus.icon}
                alt={building.sensorStatus.textBuilding}
                title={building.sensorStatus.textBuilding}
                width={isSmartphone ? "60px" : "80px"}
              />
            </IconCell>
          </Cell>
          <Cell
            sx={{ cursor: "pointer" }}
            onClick={(e) => handleCellClick(e.target)}
          >
            <CellCentered>{building.BuildingName}</CellCentered>
          </Cell>
          <Cell>
            <CellCentered>
              {building.BuildingImage === "" ? (
                <Button
                  size="medium"
                  endIcon={<OpenInNew />}
                  variant="contained"
                  disabled
                  sx={{ textWrap: "nowrap" }}
                >
                  表示
                </Button>
              ) : (
                <Button
                  size="medium"
                  endIcon={<OpenInNew />}
                  variant="contained"
                  onClick={() => handleShowMap(building.BuildingImage)}
                  sx={{ textWrap: "nowrap" }}
                >
                  表示
                </Button>
              )}
            </CellCentered>
          </Cell>
        </TableRow>
      );
    })
    .filter((value: any) => value !== null);

  if (rows.length === 0) {
    // 表示する建物がない
    return (
      <Section>
        <AndonTitleBuilding redrawSection={redraw} />
      </Section>
    );
  }

  const handleChangeSortOrder = (param: AndonParamName) => {
    if (andon.sortParamBuilding.param !== param) {
      andon.sortParamBuilding.param = param;
      andon.sortParamBuilding.order = "asc";
    } else {
      switch (andon.sortParamBuilding.order) {
        case "asc":
          andon.sortParamBuilding.order = "desc";
          break;
        case "desc":
          andon.sortParamBuilding.order = "asc";
      }
    }
    andon.Sort(andon.sortParamBuilding);
    redraw();
  };

  const GetSortExp = (param: AndonParamName) => {
    if (andon.sortParamBuilding.param !== param) {
      return "";
    }
    switch (andon.sortParamBuilding.order) {
      case "asc":
        return "▲";
      case "desc":
        return "▼";
      default:
        return "";
    }
  };

  return (
    <Section>
      <AndonTitleBuilding redrawSection={redraw} />
      <TableRange className="scrollBar">
        <table
          style={{
            borderCollapse: "collapse",
            border: "solid 1px #aaa",
            width: "100%",
          }}
        >
          <tbody>
            <tr>
              <Header
                onClick={() => handleChangeSortOrder("STATUS")}
                style={{ cursor: "pointer" }}
              >
                {"STATUS" + GetSortExp("STATUS")}
              </Header>
              <Header
                onClick={() => handleChangeSortOrder("建物名")}
                style={{ cursor: "pointer" }}
              >
                {"建物名" + GetSortExp("建物名")}
              </Header>
              <Header>詳細場所</Header>
            </tr>
            {rows}
          </tbody>
        </table>
      </TableRange>
    </Section>
  );
};

export const SensorSection = ({
  setShowDlg,
  setDlgData,
  redraw,
}: {
  setShowDlg: (bShow: boolean) => void;
  setDlgData: (data: DataDetailDlg) => void;
  redraw: () => void;
}) => {
  const andon = useContext(AppContext).GetAndonData();
  const sensors = andon.sensors;

  const isSmartphone = useMediaQuery({
    query: "(max-width: 600px)",
  });

  if (sensors.length === 0) {
    // センサがない
    return null;
  }

  const handleShowDetail = (index: number) => {
    andon.Sort(andon.sortParamBuilding);
    const data = new DataDetailDlg(
      "sensor",
      sensors[index].SensorName,
      sensors[index].StatusUpdatedAt(),
      sensors[index].UseStarted(),
      sensors[index].Expiration(),
      sensors[index].GetLeakageExpression(),
      sensors[index].GetDetails()
    );
    setShowDlg(true);
    setDlgData(data);
  };

  const handleShowMap = (url: string) => {
    window.open(url, "_blank");
  };

  const rows = sensors
    .map((sensor: Sensor, index: number) => {
      if (!sensor.bShowTop) {
        return null;
      }
      const exp = sensor.GetLeakageExpression();
      return (
        <TableRow hover={true} key={sensor.SensorId}>
          <Cell
            sx={{ cursor: "pointer" }}
            onClick={() => handleShowDetail(index)}
          >
            <IconCell>
              {exp.icon !== None ? (
                <img
                  src={exp.icon}
                  alt={exp.textSensor}
                  title={exp.textSensor}
                  width={isSmartphone ? "60px" : "80px"}
                />
              ) : (
                exp.textSensor
              )}
            </IconCell>
          </Cell>
          <Cell
            sx={{ cursor: "pointer" }}
            onClick={() => handleShowDetail(index)}
          >
            <CellCentered>{sensor.StatusUpdatedAt()}</CellCentered>
          </Cell>
          <Cell
            sx={{ cursor: "pointer" }}
            onClick={() => handleShowDetail(index)}
          >
            <CellCentered>{sensor.SensorName}</CellCentered>
          </Cell>
          <Cell
            sx={{ cursor: "pointer" }}
            onClick={() => handleShowDetail(index)}
          >
            <CellCentered>{sensor.BuildingName}</CellCentered>
          </Cell>
          <Cell
            sx={{ cursor: "pointer" }}
            onClick={() => handleShowDetail(index)}
          >
            <CellCentered>{sensor.LocationName}</CellCentered>
          </Cell>
          <Cell>
            <CellCentered>
              {sensor.SensorImage === "" ? (
                <Button
                  size="medium"
                  endIcon={<OpenInNew />}
                  variant="contained"
                  disabled
                  sx={{ textWrap: "nowrap" }}
                >
                  表示
                </Button>
              ) : (
                <Button
                  size="medium"
                  endIcon={<OpenInNew />}
                  variant="contained"
                  onClick={() => handleShowMap(sensor.SensorImage)}
                  sx={{ textWrap: "nowrap" }}
                >
                  表示
                </Button>
              )}
            </CellCentered>
          </Cell>
        </TableRow>
      );
    })
    .filter((value: any) => value !== null);

  if (rows.length === 0) {
    // 表示するセンサがない
    return (
      <Section>
        <AndonTitleSensor redrawSection={redraw} />
      </Section>
    );
  }

  const handleChangeSortOrder = (param: AndonParamName) => {
    if (andon.sortParamSensor.param !== param) {
      andon.sortParamSensor.param = param;
      andon.sortParamSensor.order = "asc";
    } else {
      switch (andon.sortParamSensor.order) {
        case "asc":
          andon.sortParamSensor.order = "desc";
          break;
        case "desc":
          andon.sortParamSensor.order = "asc";
      }
    }
    andon.Sort(andon.sortParamSensor);
    andon.Sort(andon.sortParamBuilding);
    redraw();
  };

  const GetSortExp = (param: AndonParamName) => {
    if (andon.sortParamSensor.param !== param) {
      return "";
    }
    switch (andon.sortParamSensor.order) {
      case "asc":
        return "▲";
      case "desc":
        return "▼";
      default:
        return "";
    }
  };

  return (
    <Section>
      <AndonTitleSensor redrawSection={redraw} />
      <TableRange className="scrollBar">
        <table
          style={{
            borderCollapse: "collapse",
            border: "solid 1px #aaa",
            width: "100%",
          }}
        >
          <tbody>
            <tr>
              <Header
                onClick={() => handleChangeSortOrder("STATUS")}
                style={{ cursor: "pointer" }}
              >
                {"STATUS" + GetSortExp("STATUS")}
              </Header>
              <Header
                onClick={() => handleChangeSortOrder("更新日時")}
                style={{ cursor: "pointer" }}
              >
                {"更新日時" + GetSortExp("更新日時")}
              </Header>
              <Header
                onClick={() => handleChangeSortOrder("端末名称")}
                style={{ cursor: "pointer" }}
              >
                {"端末名称" + GetSortExp("端末名称")}
              </Header>
              <Header
                onClick={() => handleChangeSortOrder("建物名")}
                style={{ cursor: "pointer" }}
              >
                {"建物名" + GetSortExp("建物名")}
              </Header>
              <Header
                onClick={() => handleChangeSortOrder("設置場所")}
                style={{ cursor: "pointer" }}
              >
                {"設置場所" + GetSortExp("設置場所")}
              </Header>
              <Header>詳細場所</Header>
            </tr>
            {rows}
          </tbody>
        </table>
      </TableRange>
    </Section>
  );
};

export const Pico3Section = ({
  setShowDlg,
  setDlgData,
  redraw,
}: {
  setShowDlg: (bShow: boolean) => void;
  setDlgData: (data: DataDetailDlg) => void;
  redraw: () => void;
}) => {
  const andon = useContext(AppContext).GetAndonData();
  const pico3 = andon.pico3;

  const isSmartphone = useMediaQuery({
    query: "(max-width: 600px)",
  });

  if (pico3.length === 0) {
    // pico3がない
    return null;
  }

  const handleShowDetail = (index: number) => {
    const data = new DataDetailDlg(
      "pico3",
      pico3[index].PicoName,
      pico3[index].StatusUpdatedAt(),
      "",
      "",
      pico3[index].GetLeakageExpression(),
      pico3[index].GetDetails()
    );
    setShowDlg(true);
    setDlgData(data);
  };

  const handleShowMap = (url: string) => {
    window.open(url, "_blank");
  };

  const rows = pico3
    .map((p: Pico3, index: number) => {
      if (!p.bShowTop) {
        return null;
      }
      const exp = p.GetLeakageExpression();
      return (
        <TableRow hover={true} key={p.PicoId}>
          <Cell
            sx={{ cursor: "pointer" }}
            onClick={() => handleShowDetail(index)}
          >
            <IconCell>
              {exp.icon !== None ? (
                <img
                  src={exp.icon}
                  alt={exp.textPico3}
                  title={exp.textPico3}
                  width={isSmartphone ? "60px" : "80px"}
                />
              ) : (
                exp.textPico3
              )}
            </IconCell>
          </Cell>
          <Cell
            sx={{ cursor: "pointer" }}
            onClick={() => handleShowDetail(index)}
          >
            <CellCentered>{p.StatusUpdatedAt()}</CellCentered>
          </Cell>
          <Cell
            sx={{ cursor: "pointer" }}
            onClick={() => handleShowDetail(index)}
          >
            <CellCentered>{p.PicoName}</CellCentered>
          </Cell>
          <Cell
            sx={{ cursor: "pointer" }}
            onClick={() => handleShowDetail(index)}
          >
            <CellCentered>{p.BuildingName}</CellCentered>
          </Cell>
          <Cell
            sx={{ cursor: "pointer" }}
            onClick={() => handleShowDetail(index)}
          >
            <CellCentered>{p.LocationName}</CellCentered>
          </Cell>
          <Cell>
            <CellCentered>
              {p.PicoImage === "" ? (
                <Button
                  size="medium"
                  endIcon={<OpenInNew />}
                  variant="contained"
                  disabled
                  sx={{ textWrap: "nowrap" }}
                >
                  表示
                </Button>
              ) : (
                <Button
                  size="medium"
                  endIcon={<OpenInNew />}
                  variant="contained"
                  onClick={() => handleShowMap(p.PicoImage)}
                  sx={{ textWrap: "nowrap" }}
                >
                  表示
                </Button>
              )}
            </CellCentered>
          </Cell>
        </TableRow>
      );
    })
    .filter((value: any) => value !== null);

  if (rows.length === 0) {
    // 表示するPico3がない
    return (
      <Section>
        <AndonTitlePico3 redrawSection={redraw} />
      </Section>
    );
  }

  const handleChangeSortOrder = (param: AndonParamName) => {
    if (andon.sortParamPico3.param !== param) {
      andon.sortParamPico3.param = param;
      andon.sortParamPico3.order = "asc";
    } else {
      switch (andon.sortParamPico3.order) {
        case "asc":
          andon.sortParamPico3.order = "desc";
          break;
        case "desc":
          andon.sortParamPico3.order = "asc";
      }
    }
    andon.Sort(andon.sortParamPico3);
    redraw();
  };

  const GetSortExp = (param: AndonParamName) => {
    if (andon.sortParamPico3.param !== param) {
      return "";
    }
    switch (andon.sortParamPico3.order) {
      case "asc":
        return "▲";
      case "desc":
        return "▼";
      default:
        return "";
    }
  };

  return (
    <Section>
      <AndonTitlePico3 redrawSection={redraw} />
      <TableRange className="scrollBar">
        <table
          style={{
            borderCollapse: "collapse",
            border: "solid 1px #aaa",
            width: "100%",
          }}
        >
          <tbody>
            <tr>
              <Header
                onClick={() => handleChangeSortOrder("STATUS")}
                style={{ cursor: "pointer" }}
              >
                {"STATUS" + GetSortExp("STATUS")}
              </Header>
              <Header
                onClick={() => handleChangeSortOrder("更新日時")}
                style={{ cursor: "pointer" }}
              >
                {"更新日時" + GetSortExp("更新日時")}
              </Header>
              <Header
                onClick={() => handleChangeSortOrder("端末名称")}
                style={{ cursor: "pointer" }}
              >
                {"端末名称" + GetSortExp("端末名称")}
              </Header>
              <Header
                onClick={() => handleChangeSortOrder("建物名")}
                style={{ cursor: "pointer" }}
              >
                {"建物名" + GetSortExp("建物名")}
              </Header>
              <Header
                onClick={() => handleChangeSortOrder("設置場所")}
                style={{ cursor: "pointer" }}
              >
                {"設置場所" + GetSortExp("設置場所")}
              </Header>
              <Header>詳細場所</Header>
            </tr>
            {rows}
          </tbody>
        </table>
      </TableRange>
    </Section>
  );
};
